/**
 * AssetPicker.jsx
 * ---------------
 * Dropdown component for selecting assets from the asset library.
 * Used in component forms to pick images, videos, documents, etc.
 */

import React, { useState, useEffect } from 'react';
import { Image, Upload, Eye, X, Folder } from 'lucide-react';
import assetService from '../../services/assetService';
import AssetPreview from './AssetPreview';
import { logger } from '../../utils/logger';

const AssetPicker = ({ 
  appId, 
  value = '', 
  onChange,
  label = 'Select Asset',
  fileType = 'all', // 'all', 'image', 'video', 'audio', 'document'
  placeholder = 'Choose an asset...',
  showPreview = true,
  required = false
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    if (appId) {
      loadAssets();
    }
  }, [appId]);

  useEffect(() => {
    if (value && assets.length > 0) {
      const asset = assets.find(a => a.downloadURL === value || a.path === value);
      setSelectedAsset(asset);
    }
  }, [value, assets]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const data = await assetService.listAssets(appId);
      
      // Filter by file type if specified
      const filtered = fileType === 'all' 
        ? data 
        : data.filter(asset => {
            if (fileType === 'image') return asset.fileType.startsWith('image/');
            if (fileType === 'video') return asset.fileType.startsWith('video/');
            if (fileType === 'audio') return asset.fileType.startsWith('audio/');
            if (fileType === 'document') return asset.fileType.includes('pdf') || 
                                                   asset.fileType.includes('doc') || 
                                                   asset.fileType.includes('xls');
            return true;
          });
      
      setAssets(filtered);
      logger.log('[AssetPicker] Assets loaded', { count: filtered.length });
    } catch (err) {
      logger.error('[AssetPicker] Failed to load assets', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (asset) => {
    setSelectedAsset(asset);
    onChange(asset.downloadURL); // Pass the download URL to parent
    setShowDropdown(false);
    logger.log('[AssetPicker] Asset selected', { assetId: asset.id });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      
      const metadata = {
        description: '',
        uploadedBy: 'user',
      };

      const uploadedAsset = await assetService.uploadAsset(file, appId, metadata);
      setAssets([uploadedAsset, ...assets]);
      handleSelect(uploadedAsset);
      setShowUpload(false);
      
      logger.log('[AssetPicker] Asset uploaded', { assetId: uploadedAsset.id });
    } catch (err) {
      logger.error('[AssetPicker] Upload failed', { error: err.message });
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const getFileTypeLabel = () => {
    const types = {
      all: 'all file types',
      image: 'images',
      video: 'videos',
      audio: 'audio files',
      document: 'documents'
    };
    return types[fileType] || 'files';
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Label */}
      <label style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '6px'
      }}>
        {label}
        {required && <span style={{ color: '#DC2626', marginLeft: '4px' }}>*</span>}
      </label>

      {/* Selected Asset Preview */}
      {showPreview && selectedAsset && (
        <div style={{
          marginBottom: '12px',
          position: 'relative',
          border: '2px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#F9FAFB'
        }}>
          {selectedAsset.fileType.startsWith('image/') ? (
            <div style={{ 
              height: '120px',
              backgroundImage: `url(${selectedAsset.downloadURL})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
          ) : (
            <div style={{
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF'
            }}>
              <Folder className="w-12 h-12" />
            </div>
          )}
          
          <button
            type="button"
            onClick={() => setSelectedAsset(null)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X className="w-4 h-4" />
          </button>
          
          <div style={{ padding: '12px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {selectedAsset.name}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6B7280',
              marginTop: '2px'
            }}>
              {(selectedAsset.size / 1024).toFixed(1)} KB
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Button */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            backgroundColor: 'white',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '14px',
            color: selectedAsset ? '#111827' : '#9CA3AF'
          }}
        >
          <span>
            {selectedAsset ? selectedAsset.name : placeholder}
          </span>
          <Folder className="w-5 h-5" style={{ color: '#9CA3AF' }} />
        </button>

        {/* Dropdown Menu */}
        {showDropdown && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 100
          }}>
            {/* Upload Button */}
            <div style={{
              padding: '8px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: '#3B82F6',
                color: 'white',
                borderRadius: '6px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
                fontSize: '13px',
                fontWeight: '600'
              }}>
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload New Asset'}
                <input
                  type="file"
                  onChange={handleUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                  accept={
                    fileType === 'image' ? 'image/*' :
                    fileType === 'video' ? 'video/*' :
                    fileType === 'audio' ? 'audio/*' :
                    fileType === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx' :
                    '*'
                  }
                />
              </label>
            </div>

            {/* Asset List */}
            {loading ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6B7280',
                fontSize: '13px'
              }}>
                Loading assets...
              </div>
            ) : assets.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6B7280',
                fontSize: '13px'
              }}>
                No {getFileTypeLabel()} available
              </div>
            ) : (
              <div>
                {assets.map(asset => (
                  <div
                    key={asset.id}
                    onClick={() => handleSelect(asset)}
                    style={{
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #F3F4F6',
                      backgroundColor: selectedAsset?.id === asset.id ? '#EFF6FF' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAsset?.id !== asset.id) {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAsset?.id !== asset.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '6px',
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {asset.fileType.startsWith('image/') ? (
                        <img
                          src={asset.downloadURL}
                          alt={asset.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Image className="w-5 h-5" style={{ color: '#9CA3AF' }} />
                      )}
                    </div>

                    {/* Asset Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {asset.name}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6B7280',
                        marginTop: '2px'
                      }}>
                        {(asset.size / 1024).toFixed(1)} KB
                      </div>
                    </div>

                    {/* Preview Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewAsset(asset);
                      }}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Eye className="w-4 h-4" style={{ color: '#6B7280' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewAsset && (
        <div
          onClick={() => setPreviewAsset(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setPreviewAsset(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <X className="w-5 h-5" />
            </button>
            
            <AssetPreview asset={previewAsset} />
          </div>
        </div>
      )}

      {/* Help Text */}
      <div style={{
        marginTop: '6px',
        fontSize: '11px',
        color: '#6B7280'
      }}>
        Select from existing {getFileTypeLabel()} or upload a new one
      </div>
    </div>
  );
};

export default AssetPicker;









